import type { ReactElement } from 'react';
import { useTranslations } from 'use-intl';
import {
  Body,
  Button,
  Card,
  Caption,
  Heading,
  I,
  Row,
  Stack,
} from '@leanin/ui';

import {
  resourceKindLabel,
  type ResourceKind,
} from '../resources/resource-adapters';
import type { LearningTrackItem } from '@/lib/queries/learning-tracks';

type Props = {
  readonly item: LearningTrackItem;
  readonly dragHandle: ReactElement;
  readonly reorderButton: ReactElement;
  readonly onRemove: () => void;
  readonly removing: boolean;
};

// Compact admin-only sortable row for a Learning Track item. Renders the
// drag handle on the left, the resource summary in the middle, and a
// destructive remove button on the right. The touch-a11y reorder button is
// surfaced in the meta row so screen reader / touch users have an alternate
// path to reorder without dragging.
//
// `item.resource === null` means the underlying resource is no longer
// visible (deleted, unpublished, network-membership-gated out). Admins can
// still detach the stale junction.
export function LearningTrackResourceRow({
  item,
  dragHandle,
  reorderButton,
  onRemove,
  removing,
}: Props) {
  const t = useTranslations('LearningTracks');
  const tr = useTranslations('Resources');
  const { resource } = item;
  const isArchived = resource === null;
  const kindLabel = isArchived
    ? t('resourceRow.kindFallback')
    : resourceKindLabel(resource.kind as ResourceKind, tr);
  const title = isArchived ? t('resourceRow.removedTitle') : resource.title;

  return (
    <Card pad="compact" direction="row" style={{ opacity: isArchived ? 0.6 : 1 }}>
      <Row gap="md" align="center" style={{ flex: 1, minWidth: 0 }}>
        {dragHandle}
        <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
          <Heading level={5}>{title}</Heading>
          <Row gap="sm" align="center" wrap>
            <Caption>{kindLabel}</Caption>
            {reorderButton}
          </Row>
          {isArchived ? (
            <Body size="sm" color="mute">
              {t('resourceRow.removedBody')}
            </Body>
          ) : null}
        </Stack>
        <Button
          variant="secondary"
          size="sm"
          icon={I.trash}
          aria-label={t('resourceRow.removeAria', { title })}
          onClick={onRemove}
          disabled={removing}
        />
      </Row>
    </Card>
  );
}
